<template>
    <div v-if="maxPrice > 0" class="slice barometer-job__specialities">
        <div class="slice-content">
            <h2 class="slice-title">{{ title }}</h2>
            <p>{{ subtitle }}</p>
            <ul class="barometer-job__specialities__list">
                <li v-for="(jobAndPrice, i) in jobAndPrices" :key="i" class="barometer-job__speciality" :class="{hidden: i >= nbShown && !showAll}">
                    <NuxtLink :to="jobAndPrice.url" class="barometer-job__speciality__label stretched-link">
                        {{ jobAndPrice.label }}
                    </NuxtLink>
                    <div class="barometer-job__speciality__value">
                        <div class="barometer-job__speciality__bar" :style="{width: `calc(${(jobAndPrice.price / maxPrice) * 100}% - 60px)`}" />
                        <div class="barometer-job__speciality__price">{{ formatPrice(jobAndPrice.price) }}</div>
                    </div>
                </li>
            </ul>
            <div v-if="!showAll && jobAndPrices.length > nbShown" class="joy-mt-10">
                <VJoyButton variant="secondary" @click="showAll = true">{{ t('generic.show.more') }}</VJoyButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {inject, ref} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import {currenciesHelper} from '~/composables/currenciesHelper';
    import {BAROMETER_JOB} from '~/keys';

    const {jobBarometer} = inject(BAROMETER_JOB)!;
    const {formatPrice} = currenciesHelper();
    const {t} = useTranslation();

    const showAll = ref(false);
    const nbShown = 4;
    const htmlTitle = t('barometer.family.average.price.specialities.title');
    const [title, subtitle] = htmlTitle.match(/>([^<]+)</g)?.map((match) => match.slice(1, -1).trim()) || [];

    const jobAndPrices = jobBarometer.jobAndPrices.filter((jobAndPrice) => jobAndPrice.price > 0).sort((a, b) => b.price - a.price);
    const maxPrice = Math.round(jobAndPrices.reduce((acc, jobAndPrice) => Math.max(acc, jobAndPrice.price), 0));
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $label-width: 220px;
    $bar-width: 400px;
    $price-width: 60px;
    $total-width: $label-width + $bar-width;

    .barometer-job__specialities {
        &__list {
            max-width: $total-width;
            list-style-type: none;
            margin: var(--joy-core-spacing-8) auto 0;

            @include mq.screen_md(min) {
                display: inline-block;
                width: 100%;
            }
        }

        .hidden {
            display: none;
        }
    }

    .barometer-job__speciality {
        text-align: left;
        position: relative;
        gap: var(--joy-core-spacing-2);
        margin-bottom: var(--joy-core-spacing-4);
        cursor: pointer;
        color: var(--joy-color-text-body);
        min-height: var(--joy-core-spacing-8);

        @include mq.screen_md(min) {
            display: flex;
            align-items: center;
        }

        &__label {
            display: block;
            margin-bottom: var(--joy-core-spacing-2);
            line-height: var(--joy-line-height-small);
            font-weight: var(--joy-font-weight-bold);
            color: var(--joy-color-text-body);

            @include mq.screen_md(min) {
                flex: none;
                width: $label-width;
                text-align: right;
                margin-bottom: 0;
            }
        }

        &__value {
            position: relative;
            flex: 1;
            display: flex;
            align-items: center;
        }

        &__bar {
            flex: none;
            height: var(--joy-core-spacing-5);
            border-radius: var(--joy-core-radius-2) var(--joy-core-radius-4) var(--joy-core-radius-4) var(--joy-core-radius-2);
            background-color: var(--joy-color-primary-50);
            opacity: 0.5;
        }

        &__price {
            transform: translateY(-2px);
            padding-left: var(--joy-core-spacing-1);
            right: calc(-1 * $price-width);
            width: $price-width;
            white-space: nowrap;
            line-height: 1;
            font-size: var(--joy-font-size-primary-600);
        }

        &:hover & {
            &__label {
                text-decoration: underline;
            }
            &__bar {
                opacity: 1;
            }
        }
    }
</style>
